import { Box, Typography } from '@mui/material'

import { housePartsIcons } from '@/assets/icons/house-parts'
import CustomCheckbox from '@/shared/components/CustomCheckbox'
import type { CustomAssessmentInput } from '@/shared/types/custom-assessment-input.types'

import homeIcon from '../../assets/home.svg'
import keyIcon from '../../assets/key.svg'
import kfwIcon from '../../assets/kfw.svg'

interface FundingContentProps {
  assessmentData: CustomAssessmentInput | null
  setAssessmentData: (data: CustomAssessmentInput) => void
}

const FundingContent = ({ assessmentData, setAssessmentData }: FundingContentProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '16px',
        }}
      >
        <Box
          onClick={() => {
            if (!assessmentData) return
            setAssessmentData({
              ...assessmentData,
              subsidyInput: {
                ...assessmentData.subsidyInput,
                heatPump: {
                  ...assessmentData.subsidyInput.heatPump,
                  buildingIsFullyRented: false,
                },
              },
            })
          }}
          sx={{
            position: 'relative',
            flex: 1,
            height: '128px',
            border: `0.5px solid ${
              assessmentData?.subsidyInput.heatPump.buildingIsFullyRented === false
                ? '#5BC06B'
                : '#E1E1E4'
            }`,
            borderRadius: '12px',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            backgroundColor:
              assessmentData?.subsidyInput.heatPump.buildingIsFullyRented === false
                ? '#E5F5E7'
                : '#F5F5F7',
            cursor: 'pointer',
          }}
        >
          <CustomCheckbox
            checkboxType="circle"
            checked={assessmentData?.subsidyInput.heatPump.buildingIsFullyRented === false}
            sx={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              width: '18px',
              height: '18px',
              padding: 0,
              pointerEvents: 'none',
            }}
          />
          <Box
            component="img"
            src={homeIcon}
            alt="(Teilweise) Selbstnutzung"
            sx={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
            }}
          />
          <Typography>(Teilweise) Selbstnutzung</Typography>
        </Box>
        <Box
          onClick={() => {
            if (!assessmentData) return
            setAssessmentData({
              ...assessmentData,
              subsidyInput: {
                ...assessmentData.subsidyInput,
                heatPump: {
                  ...assessmentData.subsidyInput.heatPump,
                  buildingIsFullyRented: true,
                },
              },
            })
          }}
          sx={{
            position: 'relative',
            flex: 1,
            height: '128px',
            border: `0.5px solid ${
              assessmentData?.subsidyInput.heatPump.buildingIsFullyRented === true
                ? '#5BC06B'
                : '#E1E1E4'
            }`,
            borderRadius: '12px',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            backgroundColor:
              assessmentData?.subsidyInput.heatPump.buildingIsFullyRented === true
                ? '#E5F5E7'
                : '#F5F5F7',
            cursor: 'pointer',
          }}
        >
          <CustomCheckbox
            checkboxType="circle"
            checked={assessmentData?.subsidyInput.heatPump.buildingIsFullyRented === true}
            sx={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              width: '18px',
              height: '18px',
              padding: 0,
              pointerEvents: 'none',
            }}
          />
          <Box
            component="img"
            src={keyIcon}
            alt="Vollständig vermietet"
            sx={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
            }}
          />
          <Typography>Vollständig vermietet</Typography>
        </Box>
      </Box>

      <Box sx={{ height: '1px', backgroundColor: '#E1E1E4' }} />

      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '14px',
        }}
      >
        Förderhöhe für die Wärmepumpe anpassen
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src={housePartsIcons.heatpump}
          alt="Wärmepumpe"
          sx={{
            height: '80px',
            objectFit: 'contain',
          }}
        />
        <Box
          component="img"
          src={kfwIcon}
          alt="KfW"
          sx={{
            height: '40px',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            borderRadius: '8px',
            padding: '16px',
            gap: '8px',
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            alignItems: 'center',
          }}
        >
          <CustomCheckbox
            checked
            disabled
            sx={{
              padding: 0,
              pointerEvents: 'none',
              opacity: 0.4,
            }}
          />
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            30 % Grundförderung
          </Typography>
        </Box>

        <Box
          onClick={() => {
            if (!assessmentData) return
            setAssessmentData({
              ...assessmentData,
              subsidyInput: {
                ...assessmentData.subsidyInput,
                heatPump: {
                  ...assessmentData.subsidyInput.heatPump,
                  eligibleForEfficiencyBonus:
                    !assessmentData.subsidyInput.heatPump.eligibleForEfficiencyBonus,
                },
              },
            })
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            borderRadius: '8px',
            padding: '16px',
            gap: '8px',
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <CustomCheckbox
            checked={assessmentData?.subsidyInput.heatPump.eligibleForEfficiencyBonus}
            sx={{ padding: 0, pointerEvents: 'none' }}
          />
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            5 % Effizienz-Bonus
          </Typography>
        </Box>

        <Box
          onClick={() => {
            if (!assessmentData) return
            setAssessmentData({
              ...assessmentData,
              subsidyInput: {
                ...assessmentData.subsidyInput,
                heatPump: {
                  ...assessmentData.subsidyInput.heatPump,
                  eligibleForSpeedBonus:
                    !assessmentData.subsidyInput.heatPump.eligibleForSpeedBonus,
                },
              },
            })
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            borderRadius: '8px',
            padding: '16px',
            gap: '8px',
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <CustomCheckbox
            checked={assessmentData?.subsidyInput.heatPump.eligibleForSpeedBonus}
            sx={{ padding: 0, pointerEvents: 'none' }}
          />
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            20 % Klimageschwindingkeits-Bonus
          </Typography>
        </Box>

        <Box
          onClick={() => {
            if (!assessmentData) return
            setAssessmentData({
              ...assessmentData,
              subsidyInput: {
                ...assessmentData.subsidyInput,
                heatPump: {
                  ...assessmentData.subsidyInput.heatPump,
                  eligibleForHouseholdIncomeBonus:
                    !assessmentData.subsidyInput.heatPump.eligibleForHouseholdIncomeBonus,
                },
              },
            })
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            borderRadius: '8px',
            padding: '16px',
            gap: '8px',
            backgroundColor: '#FAFAFB',
            border: '1px solid #F5F5F7',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <CustomCheckbox
            checked={assessmentData?.subsidyInput.heatPump.eligibleForHouseholdIncomeBonus}
            sx={{ padding: 0, pointerEvents: 'none' }}
          />
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            30 % Haushaltseinkommen-Bonus
          </Typography>
        </Box>
      </Box>

      <Typography
        component="ul"
        sx={{
          margin: 0,
          marginTop: '8px',
          paddingLeft: '24px',
          fontSize: '14px',
          lineHeight: '24px',
          color: '#464A54',
        }}
      >
        <li>
          <Typography component="span" sx={{ fontSize: '14px', lineHeight: '24px' }}>
            5 % Effizienz-Bonus für Wärmepumpen mit dem <strong>Kältemittel R290</strong>
          </Typography>
        </li>
        <li>
          <Typography component="span" sx={{ fontSize: '14px', lineHeight: '24px' }}>
            20 % Klimabonus für den Austausch von{' '}
            <strong>Ölheizungen, Kohleheizungen, Nachtspeicheröfen</strong>, oder für{' '}
            <strong>Gasheizungen älter als 20 Jahre</strong>.
          </Typography>
        </li>
        <li>
          <Typography component="span" sx={{ fontSize: '14px', lineHeight: '24px' }}>
            30 % Haushaltseinkommen-Bonus bei einem zu versteuernden{' '}
            <strong>Haushaltseinkommen bis 40.000 €</strong>
          </Typography>
        </li>
      </Typography>

      <Box sx={{ marginBottom: '8px' }}>
        <Typography sx={{ fontSize: '14px', color: '#464A54' }}>
          Der Höchstfördersatz beträgt 70 %.
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#464A54' }}>
          Die Förderung wird auf maximal 30.000 € gewährt.
        </Typography>
      </Box>
    </Box>
  )
}

export default FundingContent
